import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

const ClientLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
    const [session, setSession] = useState('');
    // MFA state: null | 'setup' (enrol TOTP) | 'code' (enter TOTP code)
    const [mfaStep, setMfaStep] = useState(null);
    const [secretCode, setSecretCode] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetStep, setResetStep] = useState('request'); // 'request', 'code', 'success'
    const [verificationCode, setVerificationCode] = useState('');
    const [resetNewPassword, setResetNewPassword] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState('');
    const [sessionExpired, setSessionExpired] = useState(false);
    const [accountDisabled, setAccountDisabled] = useState(false);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Check for expired session / disabled account on mount
    useEffect(() => {
        if (searchParams.get('expired') === 'true') {
            setSessionExpired(true);
            // Clear the expired param from URL
            searchParams.delete('expired');
            setSearchParams(searchParams, { replace: true });
        }
        if (searchParams.get('disabled') === 'true') {
            setAccountDisabled(true);
            searchParams.delete('disabled');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/client/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            const data = await response.json();

            if (response.ok) {
                if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
                    setRequiresPasswordChange(true);
                    setSession(data.session);
                } else if (data.challengeName === 'MFA_SETUP') {
                    setSession(data.session);
                    setSecretCode(data.secretCode);
                    setMfaStep('setup');
                } else if (data.challengeName === 'SOFTWARE_TOKEN_MFA') {
                    setSession(data.session);
                    setMfaStep('code');
                } else {
                    storeTokensAndGo(data);
                }
            } else if (response.status === 503 && data.maintenance) {
                setError('The portal is temporarily unavailable for maintenance. Please try again later.');
            } else {
                setError(data.error || 'Login failed');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const storeTokensAndGo = (data) => {
        // A different user logging in on this browser must not inherit the
        // previous user's view state (e.g. expanded Epics). viewStateLeadId
        // survives logout (clearTokens removes clientLeadId), so the SAME
        // user keeps their persisted state across sessions while a
        // different user gets factory defaults.
        const viewStateOwner = localStorage.getItem('viewStateLeadId');
        if (String(viewStateOwner) !== String(data.leadId)) {
            ['epicsCollapsed', 'collapsedEpics', 'collapsedSwimlanes', 'kanban-compact-swimlanes',
             'ganttScale', 'hideCompletedSubtasks', 'risksIssuesCollapsed',
             'taskDepsExpanded', 'taskLinksExpanded', 'taskScriptsExpanded',
             'ganttHideCompleted', 'kanbanHideCompleted',
             'epicsHideCompleted'].forEach(k => localStorage.removeItem(k));
        }
        localStorage.setItem('viewStateLeadId', String(data.leadId));
        localStorage.setItem('clientToken', data.token);
        localStorage.setItem('clientAccessToken', data.accessToken);
        localStorage.setItem('clientRefreshToken', data.refreshToken);
        localStorage.setItem('clientEmail', email);
        localStorage.setItem('clientLeadId', data.leadId);
        localStorage.setItem('clientTimestamp', data.timestamp);
        localStorage.setItem('clientRole', data.clientRole);
        navigate('/client/project');
    };

    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        const endpoint = mfaStep === 'setup' ? '/client/mfa/verify-setup' : '/client/mfa/respond';
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, session, code: mfaCode })
            });
            const data = await response.json();

            if (response.ok) {
                storeTokensAndGo(data);
            } else {
                setError(data.error || 'MFA verification failed');
                if ((data.error || '').includes('Session expired')) {
                    setMfaStep(null);
                    setMfaCode('');
                }
            }
        } catch (err) {
            console.error('MFA error:', err);
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const otpauthUri = `otpauth://totp/${encodeURIComponent('Migration Experts Portal')}:${encodeURIComponent(email)}?secret=${secretCode}&issuer=${encodeURIComponent('The Migration Experts')}`;

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/client/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, session, newPassword })
            });

            const data = await response.json();

            if (response.ok) {
                storeTokensAndGo(data);
            } else {
                setError(data.error || 'Password change failed');
            }
        } catch (err) {
            console.error('Password change error:', err);
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestReset = async () => {
        setResetLoading(true);
        setResetError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/client/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail })
            });

            const data = await response.json();

            if (response.ok) {
                setResetStep('code');
            } else {
                setResetError(data.error || 'Failed to send reset code');
            }
        } catch (err) {
            setResetError('Connection error. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    const handleConfirmReset = async () => {
        setResetLoading(true);
        setResetError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/client/confirm-forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: resetEmail,
                    code: verificationCode,
                    newPassword: resetNewPassword
                })
            });

            const data = await response.json();

            if (response.ok) {
                setResetStep('success');
            } else {
                setResetError(data.error || 'Failed to reset password');
            }
        } catch (err) {
            setResetError('Connection error. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-xl p-8">
        <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Client Portal</h1>
        <p className="text-gray-600 mt-2">The Migration <span className="text-red-900">Experts</span></p>
        </div>

        {sessionExpired && (
            <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
            Your session has expired. Please log in again.
            </div>
        )}

        {accountDisabled && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            Your account has been disabled. Contact your project administrator.
            </div>
        )}

        {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
            </div>
        )}

        {mfaStep ? (
            <form onSubmit={handleMfaSubmit}>
            {mfaStep === 'setup' && (
                <>
                <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
                Multi-factor authentication setup: scan this QR code with your
                authenticator app, then enter the 6-digit code.
                </div>
                <div className="flex justify-center mb-4">
                <QRCodeSVG value={otpauthUri} size={180} />
                </div>
                <p className="text-xs text-gray-500 mb-4 break-all">
                Manual entry key: {secretCode}
                </p>
                </>
            )}
            {mfaStep === 'code' && (
                <p className="text-gray-600 mb-4">
                Enter the 6-digit code from your authenticator app.
                </p>
            )}
            <input
            type="text"
            inputMode="numeric"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            placeholder="Authenticator code"
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoComplete="one-time-code"
            required
            />
            <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
            {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button
            type="button"
            onClick={() => {
                setMfaStep(null);
                setMfaCode('');
                setError('');
            }}
            className="w-full mt-2 py-2 px-4 border border-gray-300 rounded-md hover:bg-gray-50"
            >
            Back to Login
            </button>
            </form>
        ) : !requiresPasswordChange ? (
            <form onSubmit={handleLogin}>
            <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
            </label>
            <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
            autoComplete="email"
            />
            </div>

            <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
            Password
            </label>
            <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
            autoComplete="current-password"
            />
            </div>

            <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
            {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="mt-4 text-center">
            <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-primary-600 hover:text-primary-800"
            >
            Forgot Password?
            </button>
            </div>
            </form>
        ) : (
            <form onSubmit={handlePasswordChange}>
            <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
            You must change your password before continuing
            </div>

            <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
            New Password
            </label>
            <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
            minLength="8"
            />
            <p className="text-xs text-gray-500 mt-1">
            Must be at least 8 characters with uppercase, lowercase, and number
            </p>
            </div>

            <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm New Password
            </label>
            <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
            />
            </div>

            <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
            {loading ? 'Changing Password...' : 'Change Password'}
            </button>
            </form>
        )}

        <div className="mt-6 text-center">
        <a href="/" className="text-sm text-primary-600 hover:text-primary-800">
        Return to Home
        </a>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Reset Password</h2>

            {resetStep === 'request' && (
                <>
                <p className="text-gray-600 mb-4">
                Enter your email address and we'll send you a verification code.
                </p>
                <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Email Address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {resetError && (
                    <p className="text-red-600 text-sm mb-4">{resetError}</p>
                )}
                <div className="flex gap-3">
                <button
                onClick={handleRequestReset}
                disabled={resetLoading}
                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                {resetLoading ? 'Sending...' : 'Send Code'}
                </button>
                <button
                onClick={() => {
                    setShowForgotPassword(false);
                    setResetError('');
                    setResetEmail('');
                }}
                className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                Cancel
                </button>
                </div>
                </>
            )}

            {resetStep === 'code' && (
                <>
                <p className="text-gray-600 mb-4">
                If an account exists for {resetEmail}, we&apos;ve emailed you instructions
                to get back in. If you received a verification code, enter it below with
                your new password.
                </p>
                <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Verification Code"
                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <input
                type="password"
                value={resetNewPassword}
                onChange={(e) => setResetNewPassword(e.target.value)}
                placeholder="New Password (min 8 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {resetError && (
                    <p className="text-red-600 text-sm mb-4">{resetError}</p>
                )}
                <div className="flex gap-3">
                <button
                onClick={handleConfirmReset}
                disabled={resetLoading}
                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                {resetLoading ? 'Resetting...' : 'Reset Password'}
                </button>
                <button
                onClick={() => setResetStep('request')}
                className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                Back
                </button>
                </div>
                </>
            )}

            {resetStep === 'success' && (
                <div className="text-center">
                <div className="text-green-600 text-5xl mb-4">✓</div>
                <p className="text-gray-600 mb-6">
                Your password has been reset successfully. You can now log in with your new password.
                </p>
                <button
                onClick={() => {
                    setShowForgotPassword(false);
                    setResetStep('request');
                    setResetError('');
                    setResetEmail('');
                    setVerificationCode('');
                    setResetNewPassword('');
                }}
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700"
                >
                Close
                </button>
                </div>
            )}
            </div>
            </div>
        )}

        </div>
        </div>
        </div>
    );
};

export default ClientLogin;
